import { useEffect, useState } from "react";
import { Page, Progress, NavButtons, BigTextInput, ConfirmScreen, StepTitle } from "@/components/wizard";
import { SCREENING_TESTS, RESULT_CONTENT, SCREENING_DISCLAIMER_RESULT } from "@/data/screeningTests";
import { postScreeningContact } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-./()]{5,19}$/;

const navyBtn =
  "w-full bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-7 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] flex items-center justify-center cursor-pointer";
const cremaBtn =
  "w-full bg-white text-[#142D43] border border-[#E5E7EB] shadow-[0_3px_10px_rgba(20,45,67,0.06)] hover:bg-[#F4F6F8] hover:border-[rgba(20,45,67,0.25)] hover:-translate-y-[2px] active:translate-y-[1px] active:shadow-[0_1px_4px_rgba(20,45,67,0.06)] font-semibold tracking-[0.01em] px-7 rounded-[14px] transition-[background-color,border-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] flex items-center justify-center cursor-pointer";

const optionClass = (selected) =>
  `w-full min-h-[56px] px-5 rounded-[14px] text-base text-left transition-[border-color,background-color,box-shadow,transform] duration-[180ms] ${
    selected
      ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
      : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
  }`;

export default function Screening() {
  const [phase, setPhase] = useState("list");
  const [testId, setTestId] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [cStep, setCStep] = useState(0);
  const [cVals, setCVals] = useState({});
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const [idem] = useState(() => crypto.randomUUID());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [phase, qIdx, cStep]);

  const test = SCREENING_TESTS.find((t) => t.id === testId) || null;
  const totalQ = test ? test.questions.length : 0;
  const result = test && answers.filter((a) => a !== undefined).length === totalQ ? test.compute(answers) : null;

  const startIntro = (id) => {
    setTestId(id);
    setPhase("intro");
  };

  const startTest = () => {
    setQIdx(0);
    setAnswers([]);
    setPhase("test");
  };

  const answer = (v) => {
    const next = [...answers];
    next[qIdx] = v;
    setAnswers(next);
    if (qIdx < totalQ - 1) setQIdx(qIdx + 1);
  };

  const contactSteps = ["name", "phone", "email"];
  const setC = (k) => (v) => setCVals((s) => ({ ...s, [k]: v }));

  const canContactNext = () => {
    const stepId = contactSteps[cStep];
    if (stepId === "name") return (cVals.nome || "").trim().length >= 1 && (cVals.cognome || "").trim().length >= 1;
    if (stepId === "phone") return PHONE_RE.test((cVals.phone || "").trim());
    return EMAIL_RE.test((cVals.email || "").trim()) && privacy;
  };

  const submitContact = async () => {
    setSubmitting(true);
    setError("");
    try {
      await postScreeningContact({
        idempotency_key: idem,
        name: `${(cVals.nome || "").trim()} ${(cVals.cognome || "").trim()}`,
        phone: (cVals.phone || "").trim(),
        email: (cVals.email || "").trim(),
        test: test.emailLabel,
        report: result.reportLines,
        website: hp,
      });
      setPhase("done");
    } catch (e) {
      setError("Invio non riuscito. Controlla la connessione e riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "done") {
    return (
      <ConfirmScreen
        title="Richiesta inviata"
        text="Grazie. La tua richiesta è stata ricevuta. Ti ricontatteremo utilizzando i recapiti che hai indicato."
      />
    );
  }

  if (phase === "declined") {
    return (
      <ConfirmScreen
        title="Grazie"
        text="Hai completato lo screening. Non è stato richiesto alcun dato identificativo."
      />
    );
  }

  if (phase === "list") {
    return (
      <Page narrow>
        <div data-testid="screening-list">
          <h1 className="font-display mt-8 text-3xl sm:text-4xl font-semibold text-[#142D43] leading-snug">Test di screening</h1>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SCREENING_TESTS.map((t, i) => (
              <button
                type="button"
                key={t.id}
                onClick={() => startIntro(t.id)}
                data-testid={`screening-card-${t.id}`}
                className={`rounded-[18px] border border-[#E5E7EB] bg-white px-4 py-2 sm:px-5 sm:py-2.5 text-left cursor-pointer shadow-[0_6px_24px_rgba(20,45,67,0.055)] transition-[transform,box-shadow] duration-[220ms] hover:-translate-y-[2px] hover:shadow-[0_12px_30px_rgba(20,45,67,0.08)] active:translate-y-[1px] active:shadow-[0_3px_10px_rgba(20,45,67,0.06)]${i === SCREENING_TESTS.length - 1 && SCREENING_TESTS.length % 2 === 1 ? " sm:col-span-2 sm:w-[calc(50%-8px)] sm:justify-self-center" : ""}`}
              >
                <h2 className="text-base font-semibold text-[#142D43] tracking-tight leading-tight">{t.name}</h2>
                <p className="text-xs text-[#142D43] leading-tight">{t.testName}</p>
                <p className="mt-0.5 text-[11px] text-[#374151] leading-tight">{t.meta}</p>
              </button>
            ))}
          </div>
        </div>
      </Page>
    );
  }

  if (phase === "intro") {
    return (
      <Page narrow>
        <div data-testid="screening-intro">
          <StepTitle title={test.introTitle} sub={test.introSub} />
          <p className="mt-5 text-base text-[#142D43] leading-relaxed">{test.introText}</p>
          <ul className="mt-5 space-y-1.5 text-sm text-[#374151]" data-testid="intro-bullets">
            {test.bullets.map((b) => (
              <li key={b}>• {b}</li>
            ))}
          </ul>
          <button type="button" onClick={startTest} data-testid="screening-begin" className={`mt-8 ${navyBtn}`}>
            INIZIA IL TEST
          </button>
          <div className="mt-4">
            <button
              type="button"
              data-testid="intro-back-button"
              onClick={() => setPhase("list")}
              className="text-sm text-[#374151] hover:text-[#142D43] font-medium py-2 transition-colors"
            >
              INDIETRO
            </button>
          </div>
        </div>
      </Page>
    );
  }

  if (phase === "test") {
    const q = test.questions[qIdx];
    const qText = typeof q === "string" ? q : q.q;
    const section = typeof q === "string" ? null : q.section;
    const prevQ = qIdx > 0 ? test.questions[qIdx - 1] : null;
    const prevSection = prevQ && typeof prevQ !== "string" ? prevQ.section : null;
    const showRef = test.reference && (qIdx === 0 || section !== prevSection);
    const isLast = qIdx === totalQ - 1;
    const answered = answers[qIdx] !== undefined;
    return (
      <Page narrow>
        <Progress current={qIdx + 1} total={totalQ} />
        <p className="mt-4 text-xs text-[#374151] uppercase tracking-wide" data-testid="question-counter">
          Domanda {qIdx + 1} di {totalQ}
        </p>
        <div data-testid={`screening-question-${qIdx + 1}`}>
          {showRef ? (
            <p className="mt-6 text-xs text-[#374151] uppercase tracking-wide" data-testid="screening-reference">
              {test.reference}
            </p>
          ) : null}
          {section ? (
            <p className="mt-2 text-xs font-medium text-[#142D43] uppercase tracking-wide" data-testid="screening-section">
              {section}
            </p>
          ) : null}
          <h1 className="mt-3 text-xl sm:text-2xl font-semibold text-[#142D43] leading-snug">{qText}</h1>
          <div className="mt-8 flex flex-col gap-3">
            {test.options.map((opt, i) => (
              <button
                key={opt}
                type="button"
                data-testid={`answer-${i}`}
                onClick={() => answer(i)}
                className={optionClass(answers[qIdx] === i)}
              >
                {opt}
              </button>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between gap-3">
            {qIdx > 0 ? (
              <button
                type="button"
                data-testid="screening-back-button"
                onClick={() => setQIdx(qIdx - 1)}
                className="text-sm text-[#374151] hover:text-[#142D43] font-medium py-2 transition-colors"
              >
                INDIETRO
              </button>
            ) : (
              <span />
            )}
            {isLast && answered ? (
              <button
                type="button"
                data-testid="screening-result-button"
                onClick={() => setPhase("result")}
                className={navyBtn}
              >
                VEDI IL RISULTATO
              </button>
            ) : null}
          </div>
        </div>
      </Page>
    );
  }

  if (phase === "result") {
    const rc = RESULT_CONTENT[result.esito];
    return (
      <Page narrow>
        <div data-testid="screening-result">
          <h1 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-[#142D43]" data-testid="result-title">
            {rc.title}
          </h1>
          <div className="mt-4 space-y-1">
            {result.scoreLines.map((l) => (
              <p key={l} className="text-base font-medium text-[#142D43]" data-testid="result-score-line">
                {l}
              </p>
            ))}
          </div>
          <p className="mt-4 text-base text-[#142D43] leading-relaxed" data-testid="screening-result-text">
            {rc.text}
          </p>
          {result.note ? (
            <p className="mt-3 text-sm text-[#374151] leading-relaxed" data-testid="result-note">
              {result.note}
            </p>
          ) : null}
          {result.item9Alert ? (
            <div className="mt-5 rounded-xl border border-[#E5E7EB] bg-[#F4F6F8] p-4" data-testid="item9-alert">
              <p className="text-sm text-[#142D43] leading-relaxed">{result.item9Alert}</p>
            </div>
          ) : null}
          <p className="mt-4 text-xs text-[#374151] leading-relaxed" data-testid="screening-disclaimer">
            {SCREENING_DISCLAIMER_RESULT}
          </p>

          <div className="mt-6 border-t border-[#E5E7EB] pt-5">
            <h2 className="text-base sm:text-lg font-semibold text-[#142D43]">Vuoi richiedere un primo colloquio?</h2>
            <p className="mt-1 text-sm text-[#374151]">
              Se desideri che ti ricontattiamo, puoi lasciarci i tuoi dati. Questa parte è facoltativa.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                data-testid="result-contact-yes"
                onClick={() => {
                  setCStep(0);
                  setPhase("contact");
                }}
                className={navyBtn}
              >
                SÌ, RICONTATTATEMI
              </button>
              <button type="button" data-testid="result-contact-no" onClick={() => setPhase("declined")} className={cremaBtn}>
                NO, GRAZIE
              </button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const stepId = contactSteps[cStep];
  const isLastContact = cStep === contactSteps.length - 1;
  return (
    <Page narrow>
      <Progress current={cStep + 1} total={3} />
      <div data-testid={`contact-step-${stepId}`}>
        {stepId === "name" && (
          <>
            <StepTitle title="Come ti chiami?" />
            <div className="mt-8 flex flex-col gap-3">
              <BigTextInput value={cVals.nome || ""} onChange={setC("nome")} placeholder="Nome" testId="contact-nome" autoFocus />
              <BigTextInput value={cVals.cognome || ""} onChange={setC("cognome")} placeholder="Cognome" testId="contact-cognome" />
            </div>
          </>
        )}
        {stepId === "phone" && (
          <>
            <StepTitle title="A quale numero possiamo contattarti?" />
            <BigTextInput value={cVals.phone || ""} onChange={setC("phone")} placeholder="+41 79 000 00 00" testId="contact-phone" type="tel" />
          </>
        )}
        {stepId === "email" && (
          <>
            <StepTitle title="Qual è il tuo indirizzo email?" />
            <BigTextInput value={cVals.email || ""} onChange={setC("email")} placeholder="nome@email.com" testId="contact-email" type="email" />
            <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="privacy-consent">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                data-testid="privacy-checkbox"
                className="mt-1 h-5 w-5 shrink-0 accent-[#142D43]"
              />
              <span className="text-sm text-[#142D43] leading-relaxed">
                Ho letto l'informativa privacy e acconsento al trattamento dei miei dati, necessario per poterti ricontattare.
              </span>
            </label>
          </>
        )}

        {error ? (
          <p className="mt-4 text-sm text-red-700" data-testid="contact-error">
            {error}
          </p>
        ) : null}

        <input
          type="text"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] opacity-0 h-0 w-0"
        />

        <NavButtons
          onBack={() => (cStep === 0 ? setPhase("result") : setCStep(cStep - 1))}
          onNext={() => (isLastContact ? submitContact() : setCStep(cStep + 1))}
          canNext={canContactNext()}
          nextLabel={isLastContact ? "INVIA RICHIESTA" : "CONTINUA"}
          nextTestId={isLastContact ? "contact-submit-button" : "wizard-next-button"}
          loading={submitting}
        />
      </div>
    </Page>
  );
}
